import React, { FunctionComponent, useState, useEffect, useCallback, BaseSyntheticEvent } from 'react'
import { Form, FormikProps } from 'formik'
import { setCountryInCookie } from '@common/utils/cookie-utils'
import { CommonButton, ConvertStringToReact, LoadingSpinner } from '@common/components'
import { isEmpty } from 'lodash-es'
import { getSessionState, saveSessionState, connectRedux, GoToComponentProps, scrollToTop } from '@common/utils'
import { useQualtrics } from '@common/hooks'
import {
    mapLmiProCheckoutFormState,
    mapLmiProCheckoutFormActions,
    LmiProCheckoutFormActionsType,
} from '@lmipro/connectedComponents/CheckoutForm/connector'
import { isAddressComplete, isValidationAddressEligible } from '@lmipro/utils'
import {
    ConnectedLmiProAccountFormInfo,
    LmiProAccountFormInfoComponentProps,
    ConnectedLmiProBillingFormInfo,
    LmiProBillingFormInfoComponentProps,
    ConnectedLmiProPaymentFormInfo,
    LmiProPaymentFormInfoComponentProps,
    ConnectedLmiProCheckoutFormSubmission,
    LmiProCheckoutFormSubmissionComponentProps,
} from '@lmipro/connectedComponents'
import { LmiProCheckoutFormState } from '@lmipro/connectedComponents/CheckoutForm/state'
import { ConnectedModal } from '@common/connectedComponents/Modal'
import {
    CHECKOUTFORM_FIELDS,
    CHECKOUTFORM_VALIDATION_EXCLUDE_FIELD_TYPES,
    LMIPRO_PARTIAL_NON_VALIDATION_USER_TYPES,
    LMIPRO_USER_TYPES,
} from '@lmipro/constants'
import { LMIPRO_TRIAL_ERROR_IDS } from '@lmipro/constants/error-strings'
import { useCheckoutFormPageTracking } from '@lmipro/hooks'
import { LmiProErrorContent } from '@lmipro/state/checkout-form-container/state'
import { LmiProBuyFormData } from '@lmipro/state/buy-form/state'
import { LMIPRO_VIEW_PATHS, TRACKING_EVENTS, PLATFORM } from '@common/constants'

import { CardinalPaymentsValidatedResponse } from '@common/state/flex/state'
import { FATAL_ERROR_TYPE } from '@common/state/fatal-error/state'
import checkoutStyles from './checkout-form.module.css'

declare const Cardinal: any

type LmiProCheckoutFormProps = GoToComponentProps<
    LmiProCheckoutFormState,
    LmiProCheckoutFormActionsType,
    {
        isTabletOrMobileView?: boolean
        isMobileView?: boolean
        initialData: LmiProBuyFormData
        errorContent: LmiProErrorContent
        formikRef: React.RefObject<FormikProps<LmiProBuyFormData>>
        setHasAddressValidationErrors: React.Dispatch<React.SetStateAction<boolean>>
        setGeneralFieldErrors: React.Dispatch<React.SetStateAction<boolean>>
        setHasCardValidationErrors: React.Dispatch<React.SetStateAction<boolean>>
        hasAddressValidationErrors: boolean
        generalFieldErrors: boolean
        hasCardValidationErrors: boolean
        isSubmittingRef: React.RefObject<boolean>
    }
>

const CheckoutForm: FunctionComponent<LmiProCheckoutFormProps> = ({ state, actions, props }) => {
    const { userInteractedWithFlexField, inlineErrors, clearEmail, content, statesOptional } = state
    const {
        formikRef,
        setHasAddressValidationErrors,
        setGeneralFieldErrors,
        setHasCardValidationErrors,
        hasCardValidationErrors,
        generalFieldErrors,
        hasAddressValidationErrors,
        isSubmittingRef,
    } = props
    const { setCheckoutFormInlineError, removeCheckoutFormInlineError } = actions

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [useSameForBilling, setUseSameForBilling] = useState(true)
    const [isFormReset, setIsFormReset] = useState(false)
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
    const [showCurrencyModal, setCurrencyModal] = useState(false)
    const [showCardinalModal, setCardinalModal] = useState(false)
    const [isTaxRequired, setTaxRequired] = useState(false)
    const [isCardinalScriptLoaded, setIsCardinalScriptLoaded] = useState(false)
    const [onInitialValue, setOnInitialValue] = useState<Record<string, string>>({})

    const hasExistingPaymentInformation = !isEmpty(state.existingPaymentInformation)
    const isAddonFlow =
        state.isUserLoggedIn &&
        (state.userType === LMIPRO_USER_TYPES.ADDON ||
            (state.userType === LMIPRO_USER_TYPES.EXPIRED && hasExistingPaymentInformation))
    const isCrossProductFlow =
        (state.isUserLoggedIn && state.userType === LMIPRO_USER_TYPES.CROSSPRODUCT && hasExistingPaymentInformation) ||
        (state.isUserLoggedIn && state.userType === LMIPRO_USER_TYPES.TRIALER && hasExistingPaymentInformation)
    const isTrialUser =
        state.userType === LMIPRO_USER_TYPES.IDENTITY_ONLY ||
        (state.userType === LMIPRO_USER_TYPES.TRIALER && !hasExistingPaymentInformation) ||
        (state.userType === LMIPRO_USER_TYPES.CROSSPRODUCT && !hasExistingPaymentInformation)
    const isExpiredTrialUser = state.userType === LMIPRO_USER_TYPES.EXPIRED
    const cardinalScriptUrl =
        process.env.ENVIRONMENT === PLATFORM.PROD
            ? 'https://songbird.cardinalcommerce.com/edge/v1/songbird.js'
            : 'https://songbirdstag.cardinalcommerce.com/edge/v1/songbird.js'
    const qualtricsQZID = process.env.ENVIRONMENT === PLATFORM.PROD ? 'ZN_299DrXKoCGOsbHw' : 'ZN_eWlJ0ywVecl7T38'

    useQualtrics(isSubmitting, qualtricsQZID)

    const resetFieldTouched = useCallback(() => {
        if (formikRef?.current) {
            Object.keys(formikRef.current.values).forEach((fieldKey) => {
                formikRef.current?.setFieldTouched(fieldKey, false)
            })
        }
    }, [formikRef])

    const canValidateAddress = (values: LmiProBuyFormData) => {
        // for later: need to send a new FormData object with billing first name and billing last name instead of FN & LN
        if (isValidationAddressEligible(values) && isAddressComplete(values, statesOptional, isTaxRequired)) {
            actions.validateAddress(values)
            actions.calculatePrice(values)
        }
    }

    const resetFatalError = () => {
        if (isFormReset && state.isFatalError) {
            actions.setFatalError(false)
            setIsFormReset(false)
        }
    }

    // tracking
    const { trackFormError, trackBillingInfoStep, trackPaymentStep, trackSubmitForm } = useCheckoutFormPageTracking({
        planName: state.selectedPlan.name,
        formikRef,
    })

    const loadCardinalScript = () => {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.async = true
        script.src = cardinalScriptUrl
        script.addEventListener('load', () => {
            setIsCardinalScriptLoaded(true)
        })
        script.addEventListener('error', () => {
            actions.setFatalError(true, { fatalErrorType: FATAL_ERROR_TYPE.CARDINAL_SETUP })
            actions.track({
                event: TRACKING_EVENTS.CARDINAL_SECURITY_MODAL,
                eventData: {
                    securityModalAction: TRACKING_EVENTS.CARDINAL_FAIL,
                },
            })
        })
        document.body.appendChild(script)
    }

    const submitForm = (formValues: LmiProBuyFormData) => {
        try {
            const qualtricsData = getSessionState('qualtrics_data_gh')
            saveSessionState('qualtrics_data_gh', {
                ...(!qualtricsData ? {} : qualtricsData),
                userEmail: formValues.Email,
            })
        } catch (err) {
            console.error(err)
        }
        setIsSubmitting(true)
        if (!userInteractedWithFlexField) {
            actions.setUserInteractedWithFlexField(true)
        }
        actions.submitForm(formValues)
        trackSubmitForm()
    }

    const onFormErrors = (formIds: string[], formErrors: string[]) => {
        scrollToTop()
        setGeneralFieldErrors(true)
        trackFormError(formIds, formErrors)
    }

    const preSubmit = () => {
        if (hasAddressValidationErrors || hasCardValidationErrors) {
            scrollToTop()
            if (hasCardValidationErrors) {
                trackFormError([LMIPRO_TRIAL_ERROR_IDS.CARD_ERROR], ['invalid credit card'])
            }
            if (hasAddressValidationErrors) {
                trackFormError([LMIPRO_TRIAL_ERROR_IDS.ADDRESS_ERROR], ['invalid billing address'])
            }
        } else if (isAddonFlow || isCrossProductFlow) {
            setIsSubmitting(true)
            trackSubmitForm()
            if (formikRef?.current) {
                actions.existingUserSubmitForm(formikRef.current.values)
            }
        } else if (formikRef?.current) {
            formikRef.current.validateForm().then((errors) => {
                if (statesOptional && errors.StateCode) {
                    /* eslint-disable no-param-reassign */
                    delete errors.StateCode
                }
                if (!isTaxRequired && errors.TaxNumber) {
                    /* eslint-disable no-param-reassign */
                    delete errors.TaxNumber
                }
                const errorList = Object.keys(errors)
                if (!state.isCardValid) {
                    actions.setUserInteractedWithFlexField(true)
                }
                if (!state.isCvvValid) {
                    actions.setUserInteractedWithFlexFieldCvv(true)
                }
                if (errors.Password && formikRef?.current) {
                    formikRef.current.touched.Password = true
                }
                if (errorList.length > 0) {
                    if (isTrialUser || isExpiredTrialUser) {
                        const trialUserValidErrors = Object.entries(errors).filter(
                            (errorField) => errorField[0] !== CHECKOUTFORM_FIELDS.PASSWORD
                        )
                        if (Object.keys(trialUserValidErrors).length > 0) {
                            const trialUserValidErrorValues = trialUserValidErrors.map(
                                (fieldErrorTuple) => fieldErrorTuple[1]
                            )
                            const trialUserValidErrorKeys = trialUserValidErrors.map(
                                (fieldErrorTuple) => fieldErrorTuple[0]
                            )
                            onFormErrors(trialUserValidErrorKeys, trialUserValidErrorValues as string[])
                        } else if (formikRef.current) submitForm(formikRef.current.values)
                    } else {
                        onFormErrors(Object.keys(errors), Object.values(errors) as string[])
                    }
                } else if (formikRef.current) {
                    submitForm(formikRef.current.values)
                }
            })
        }
    }

    const checkCurrencyConflict = (country: string, originCountry: string) => {
        if (originCountry !== country) {
            setCurrencyModal(true)
        } else {
            setCurrencyModal(false)
        }
    }

    /* configure cardinal */
    useEffect(() => {
        if (!isCardinalScriptLoaded) {
            return
        }

        Cardinal.configure({
            logging: {
                level: 'on',
            },
        })

        Cardinal.on('payments.validated', (data: CardinalPaymentsValidatedResponse, jwt: string) => {
            const {
                ErrorDescription,
                ErrorNumber,
                Payment: {
                    ExtendedData: { ChallengeCancel },
                },
            } = data
            if (ErrorDescription === 'Success' && ErrorNumber === 0 && ChallengeCancel !== '01') {
                actions.setChallengeCompleteJwt(jwt)
                actions.track({
                    event: TRACKING_EVENTS.CARDINAL_SECURITY_MODAL,
                    eventData: {
                        securityModalAction: TRACKING_EVENTS.CARDINAL_SUCCESS,
                    },
                })
            } else {
                actions.setFatalError(true, { fatalErrorType: FATAL_ERROR_TYPE.CARDINAL_CHALLENGE })
                actions.track({
                    event: TRACKING_EVENTS.CARDINAL_SECURITY_MODAL,
                    eventData: {
                        securityModalAction: TRACKING_EVENTS.CARDINAL_FAIL,
                    },
                })
            }
        })

        Cardinal.on('payments.setupComplete', () => {
            actions.setCardinalSetupComplete()
            actions.track({
                event: TRACKING_EVENTS.CARDINAL_SECURITY_MODAL,
                eventData: {
                    securityModalAction: TRACKING_EVENTS.CARDINAL_LOADED,
                },
            })
        })

        Cardinal.setup('init', {
            jwt: state.tokenString,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.tokenString, isCardinalScriptLoaded])

    useEffect(() => {
        if (!Object.keys(inlineErrors).length) {
            setGeneralFieldErrors(false)
        }
    }, [inlineErrors, setGeneralFieldErrors])

    useEffect(() => {
        if (statesOptional && formikRef.current) {
            removeCheckoutFormInlineError('StateCode')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statesOptional])

    useEffect(() => {
        if (state.userType === LMIPRO_USER_TYPES.ADDON && formikRef.current) {
            actions.calculatePrice(formikRef.current.values)
        }
    }, [state.userType, actions, formikRef])

    useEffect(() => {
        if (state.isScaEnabledForMid) {
            loadCardinalScript()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.isScaEnabledForMid])

    // show form errors by scrolling to top
    useEffect(() => {
        const errorsInForm = generalFieldErrors || hasAddressValidationErrors
        if (errorsInForm) {
            scrollToTop()
            setIsSubmitting(false)
            if ((window as any).QSI) {
                ;(window as any).QSI.API.unload()
            }
        }
    }, [hasAddressValidationErrors, generalFieldErrors])

    // reset the form and reset the states affected by checkout form
    useEffect(() => {
        if (state.isFatalError && formikRef.current) {
            scrollToTop()
            formikRef.current.setSubmitting(false)
            setIsSubmitting(false)
            resetFieldTouched()
            formikRef.current.setFieldValue(CHECKOUTFORM_FIELDS.EXPIRATION_DATE, '')
            setHasAddressValidationErrors(false)
            setHasCardValidationErrors(false)
            setGeneralFieldErrors(false)
            setIsFormReset(true)
            if (
                state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_SETUP ||
                state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_CHALLENGE ||
                state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_PAYMENT
            ) {
                setCardinalModal(true)
            }
            if ((window as any).QSI) {
                ;(window as any).QSI.API.unload()
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions, formikRef, isFormReset, resetFieldTouched, state.isFatalError])

    // validate the non empty entries after populating
    useEffect(() => {
        if (state.userType !== LMIPRO_USER_TYPES.ADDON) {
            Object.entries(props.initialData).forEach(([formField, formValue]) => {
                // ignore empty fields
                if (formValue !== '') {
                    // if the user is a trialer, crossproduct, expired, banned
                    // then dont validate the identity and address fields
                    if (
                        !(
                            Object.values(LMIPRO_PARTIAL_NON_VALIDATION_USER_TYPES).includes(
                                state.userType as LMIPRO_PARTIAL_NON_VALIDATION_USER_TYPES
                            ) &&
                            Object.values(CHECKOUTFORM_VALIDATION_EXCLUDE_FIELD_TYPES).includes(
                                formField as CHECKOUTFORM_VALIDATION_EXCLUDE_FIELD_TYPES
                            )
                        )
                    ) {
                        if (formikRef.current) {
                            formikRef.current.validateField(formField)
                        }
                    }
                }
            })
        }
    }, [formikRef, props.initialData, state.userType])

    // if the billing frequency changes, recalculate the cost
    useEffect(() => {
        if (formikRef.current && isAddressComplete(formikRef.current.values, statesOptional, isTaxRequired)) {
            actions.calculatePrice(formikRef.current.values)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions, formikRef, state.billingFrequency])

    // for trial and expired trial user, set billing FN and LN to avoid validation error
    useEffect(() => {
        if ((isTrialUser || isExpiredTrialUser) && useSameForBilling && formikRef.current) {
            formikRef.current.setFieldValue(CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME, props.initialData.FirstName)
            formikRef.current.setFieldValue(CHECKOUTFORM_FIELDS.BILLING_LAST_NAME, props.initialData.LastName)
        }
    }, [isTrialUser, useSameForBilling, props.initialData, isExpiredTrialUser, formikRef])

    const accountInfoProps: LmiProAccountFormInfoComponentProps = {
        useSameForBilling,
        resetFatalError,
        canValidateAddress,
        formikRef,
        setCheckoutFormInlineError,
        removeCheckoutFormInlineError,
        setOnInitialValue,
        inlineErrors,
        isSubmittingRef,
    }

    const billingInfoProps: LmiProBillingFormInfoComponentProps = {
        useSameForBilling,
        resetFatalError,
        canValidateAddress,
        setUseSameForBilling,
        trackBillingInfoStep,
        setHasAddressValidationErrors,
        formikRef,
        setCheckoutFormInlineError,
        removeCheckoutFormInlineError,
        inlineErrors,
        checkCurrencyConflict,
        setTaxRequired,
    }

    const paymentInfoProps: LmiProPaymentFormInfoComponentProps = {
        resetFatalError,
        hasExistingPaymentInformation,
        setIsPaymentModalOpen,
        trackPaymentStep,
        setHasCardValidationErrors,
        setCheckoutFormInlineError,
        removeCheckoutFormInlineError,
        inlineErrors,
    }

    const checkoutSubmissionProps: LmiProCheckoutFormSubmissionComponentProps = {
        isSubmitting,
        preSubmit,
        isTabletOrMobileView: props.isTabletOrMobileView,
        inlineErrors,
    }

    const trackAbandon = (value: BaseSyntheticEvent) => {
        if (formikRef.current) {
            const formData = {
                firstName: formikRef.current.values.FirstName,
                lastName: formikRef.current.values.LastName,
                email: formikRef.current.values.Email,
            }

            const fieldName = value.target.name
            const currentValue = value.target.value
            const fieldsToValidate = [
                CHECKOUTFORM_FIELDS.EMAIL,
                CHECKOUTFORM_FIELDS.FIRST_NAME,
                CHECKOUTFORM_FIELDS.LAST_NAME,
            ]

            if (fieldsToValidate.includes(fieldName)) {
                const initialValue = onInitialValue[fieldName]
                formikRef.current.validateField(fieldName).then(() => {
                    const hasFieldErrors = Boolean(
                        formikRef.current?.errors.Email ||
                            formikRef.current?.errors.FirstName ||
                            formikRef.current?.errors.LastName
                    )
                    if (!isEmpty(formData.email) && currentValue !== initialValue && !hasFieldErrors) {
                        actions.submitAbandonCart(formData.email, formData)
                    }
                })
            }
        }
    }

    return (
        <>
            <Form onBlur={(value) => trackAbandon(value)}>
                {!state.isUserLoggedIn && <ConnectedLmiProAccountFormInfo {...accountInfoProps} />}
                <ConnectedLmiProBillingFormInfo {...billingInfoProps} />
                <ConnectedLmiProPaymentFormInfo {...paymentInfoProps} />
                <ConnectedLmiProCheckoutFormSubmission {...checkoutSubmissionProps} />
            </Form>
            <ConnectedModal isOpen={showCardinalModal} closeable={false}>
                <div>
                    <h3>Oh no!</h3>
                    <div className={checkoutStyles['currency-modal-desc']}>
                        {state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_CHALLENGE
                            ? content.errorCardinalChallenge
                            : content.errorCardinalSetup}
                    </div>
                    <div>
                        <CommonButton
                            onClick={() => {
                                window.location.reload()
                            }}
                            theme="lmipro"
                        >
                            {content.modalCloseButtonText}
                        </CommonButton>
                    </div>
                </div>
            </ConnectedModal>
            <ConnectedModal isOpen={showCurrencyModal} closeable={false}>
                <div>
                    <h3>{content.changeCountryHeader}</h3>
                    <div className={checkoutStyles['currency-modal-desc']}>
                        <ConvertStringToReact htmlString={content.changeCountryContent} />
                    </div>
                    <div>
                        <CommonButton
                            onClick={() => {
                                const currentForm = formikRef.current
                                if (currentForm) {
                                    setCountryInCookie(currentForm.values.CountryCode)
                                    window.location.reload()
                                }
                            }}
                            modifier={checkoutStyles['currency-modal-cta']}
                            theme="lmipro"
                        >
                            {content.modalReload}
                        </CommonButton>
                        <CommonButton
                            purpose="quaternary"
                            onClick={() => {
                                const currentForm = formikRef.current
                                if (currentForm) currentForm.values.CountryCode = currentForm.initialValues.CountryCode
                                setCurrencyModal(false)
                            }}
                        >
                            {content.modalCancel}
                        </CommonButton>
                    </div>
                </div>
            </ConnectedModal>
            <ConnectedModal isOpen={clearEmail} closeable={false}>
                <div>
                    <h3>{content.existingUsersModalHeader}</h3>
                    <div className={checkoutStyles['existing-email-text']}>
                        <ConvertStringToReact htmlString={content.existingUsersModalContent} />
                    </div>
                    <div className={checkoutStyles['existing-email-ctas']}>
                        <CommonButton
                            onClick={() => actions.userLogin(LMIPRO_VIEW_PATHS.LMIPRO_CHECKOUT)}
                            qaTag="login-error-button"
                            theme="lmipro"
                        >
                            {content.signIn}
                        </CommonButton>
                        <CommonButton purpose="tertiary" onClick={() => actions.setEmailClear(false)} theme="lmipro">
                            {content.existingUsersNewEmailButtonText}
                        </CommonButton>
                    </div>
                </div>
            </ConnectedModal>

            <ConnectedModal isOpen={isPaymentModalOpen} closeable onClose={() => setIsPaymentModalOpen(false)}>
                <div>
                    <h3>{content.changePaymentMethodModalHeader}</h3>
                    <div>
                        <ConvertStringToReact htmlString={content.changePaymentMethodModalSubheader} />
                    </div>
                    <CommonButton onClick={() => setIsPaymentModalOpen(false)} theme="lmipro">
                        {content.modalCloseButtonText}
                    </CommonButton>
                </div>
            </ConnectedModal>

            {isSubmitting && <LoadingSpinner wrapperModifier={checkoutStyles['submit-blocker']} />}
            {/* Needed for qualtrics, Do Not Remove */}
            <div id={qualtricsQZID} />
        </>
    )
}

export const ConnectedCheckoutForm = connectRedux(
    CheckoutForm,
    mapLmiProCheckoutFormState,
    mapLmiProCheckoutFormActions
)
